<template>
  <button
    data-testid="button-base"
    :type="buttonType"
    :title="title"
    class="btn"
    :class="[
      typeClass,
      fontSizeClass,
      {
        'btn-small': small,
        'btn-medium': medium,
        'btn-big': big,
        disabled: disabled,
        'text-bold': bold,
        'text-uppercase': uppercase,
        'inline-block': inline,
        loading: loading,
        'no-padding': noPadding,
        'fit-content': fitContent
      }
    ]"
    :disabled="disabled || loading"
    @click="onClick"
  >
    <span
      v-if="$slots.prefixIcon"
      data-testid="prefix-icon"
      class="btn__prefix-icon"
      :class="iconTypeClass"
    >
      <slot name="prefixIcon" />
    </span>
    <span
      v-if="loading"
      class="btn__loader"
      :class="{ 'btn__loader--center': !isTextLoading }"
    >
      <IconLoader />
      <span v-if="isTextLoading">
        {{ $t('base.in_progress') }}
      </span>
    </span>
    <span class="btn__text">
      <slot />
    </span>
    <span
      v-if="$slots.suffixIcon"
      data-testid="suffix-icon"
      class="btn__suffix-icon"
      :class="iconTypeClass"
    >
      <slot name="suffixIcon" />
    </span>
  </button>
</template>

<script>
import IconLoader from '@/components/icons/IconLoader.vue'

export default {
  name: 'BaseButton',
  components: {
    IconLoader
  },
  props: {
    type: {
      type: String,
      default: 'primary',
      validator(value) {
        return [
          'primary',
          'secondary',
          'outline',
          'danger',
          'danger-outline',
          'edit',
          'link',
          'light',
          'dark',
          'primary-secondary',
          'white-transparent',
          'light-secondary',
          'light-primary',
          'brand'
        ].includes(value)
      }
    },
    small: {
      type: Boolean,
      default: false
    },
    medium: {
      type: Boolean,
      default: false
    },
    big: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    bold: {
      type: Boolean,
      default: false
    },
    uppercase: {
      type: Boolean,
      default: false
    },
    iconType: {
      type: String,
      default: 'inherit',
      validator(value) {
        return ['inherit', 'error', 'primary', 'success'].includes(value)
      }
    },
    buttonType: {
      type: String,
      default: 'button',
      validator(value) {
        return ['button', 'submit', 'reset'].includes(value)
      }
    },
    inline: {
      type: Boolean,
      default: false
    },
    fontSize: {
      type: String,
      default: 'middle',
      validator(value) {
        return ['small', 'middle', 'big', 'xl'].includes(value)
      }
    },
    loading: {
      type: Boolean,
      default: false
    },
    noPadding: {
      type: Boolean,
      default: false
    },
    fitContent: {
      type: Boolean,
      default: false
    },
    isTextLoading: {
      type: Boolean,
      default: true
    },
    title: {
      type: [String, Object],
      default: null
    }
  },
  emits: ['click'],
  computed: {
    typeClass() {
      return `btn-${this.type}`
    },
    iconTypeClass() {
      let { iconType } = this
      if (this.disabled) {
        iconType = 'disabled'
      }
      return `bnt__prefix-${iconType}`
    },
    fontSizeClass() {
      const { fontSize } = this
      return `font-size--${fontSize}`
    }
  },
  methods: {
    onClick(event) {
      if (this.disabled) {
        return
      }
      this.$emit('click', event)
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/components/btn';
</style>
